import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { euro } from "@/lib/format";
import { api } from "@/lib/api";

export function AuthImage({ path, className = "", alt = "foto" }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let obj;
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => setErr(true));
    return () => obj && URL.revokeObjectURL(obj);
  }, [path]);
  if (err) return <div className={`${className} bg-slate-100 flex items-center justify-center text-xs text-slate-400`}>foto non disponibile</div>;
  if (!url) return <div className={`${className} bg-slate-100 animate-pulse`} />;
  return <img src={url} alt={alt} className={className} />;
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6" data-testid="page-header">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-extrabold text-[#0B2447] tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "navy", sub, testId }) {
  const tones = {
    navy: "text-[#0B2447] bg-[#E4F5FB]",
    cyan: "text-[#0EA5C4] bg-[#E4F5FB]",
    ok: "text-[#16a34a] bg-green-50",
    warn: "text-[#f59e0b] bg-orange-50",
    err: "text-[#dc2626] bg-red-50",
  };
  return (
    <Card className="p-5 card-hover border-slate-200/70 animate-cbfade" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
          <p className="stat-num text-3xl mt-2 text-[#0B2447]">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        {Icon && <span className={`p-2.5 rounded-xl ${tones[tone]}`}><Icon size={20} /></span>}
      </div>
    </Card>
  );
}

export function Money({ cents, className = "" }) {
  return <span className={className}>{euro(cents)}</span>;
}

export function Loading({ label = "Caricamento…" }) {
  return (
    <div className="flex items-center justify-center py-24 text-slate-400 gap-2" data-testid="loading">
      <Loader2 className="animate-spin" size={20} /> {label}
    </div>
  );
}

export function EmptyState({ title = "Nessun dato", desc, icon: Icon, action }) {
  return (
    <div className="text-center py-16 text-slate-400" data-testid="empty-state">
      {Icon && <Icon size={40} className="mx-auto mb-3 opacity-50" />}
      <p className="font-semibold text-slate-600">{title}</p>
      {desc && <p className="text-sm mt-1">{desc}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

const STATUS_STYLES = {
  pending: "bg-orange-100 text-orange-700",
  verified: "bg-green-100 text-green-700",
  anomaly: "bg-red-100 text-red-700",
  completed: "bg-green-100 text-green-700",
  open: "bg-red-100 text-red-700",
  charged: "bg-slate-200 text-slate-700",
  deducted: "bg-blue-100 text-blue-700",
  waived: "bg-slate-100 text-slate-500",
  resolved: "bg-green-100 text-green-700",
  active: "bg-green-100 text-green-700",
  inactive: "bg-slate-200 text-slate-500",
  paid: "bg-green-100 text-green-700",
};

const STATUS_LABELS = {
  pending: "Da verificare", verified: "Verificata", anomaly: "Anomalia", completed: "Completato",
  open: "Aperta", charged: "Addebitata", deducted: "Scalata cauzione", waived: "Condonata",
  resolved: "Risolta", active: "Attivo", inactive: "Disattivo", paid: "Pagato",
};

export function StatusBadge({ status }) {
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600"}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}
